import React, { useEffect, useRef, useState } from 'react'
import { Mic, Square, Loader2, RotateCcw, X } from 'lucide-react'

// 🎤 DICTATE into a notes box (dictate1023, Paul 23 Oct 2026: "I want to bring in a transcription so that I can make notes
// via voice notes instead of typing it out"). Tap to record (the browser's MediaRecorder), tap to stop, at most 3 minutes.
// The clip goes to POST /dictations and is transcribed on the PRACTICE PC (the same faster-whisper worker as the WhatsApp
// voice notes, English + Afrikaans). The words are then INSERTED at the cursor; nothing already typed is replaced, and
// nothing is saved: the user reads them and saves as normal.
//
// 🚨 Never the browser's built-in speech recognition (the Web Speech API): Chrome sends that audio to Google, and notes
// can hold patient information (POPIA). script/dictation_audit.rb fails the gate if it is used anywhere in the app.
//
// If the transcriber is down, the recording stays here (Try again / Discard) until the user discards it.
//
//   const box = useRef(null)
//   <textarea ref={box} value={text} onChange={(e) => setText(e.target.value)} />
//   <DictateButton targetRef={box} onText={setText} context="stock_note" />
export const MAX_SECONDS = 180
export const UNAVAILABLE = 'Dictation unavailable right now, please type'
const POLL_MS = 2000
const GIVE_UP_MS = 11 * 60 * 1000   // past the server's own timeouts (4 min to be claimed + 6 min to answer)

// Put `words` into `value` at `caret` without touching one character already there. A space is added only where the
// words would otherwise glue onto a neighbour. Returns the new value and where the cursor lands (just after the words).
export function insertAtCursor(value, caret, words) {
  const v = String(value ?? '')
  const w = String(words ?? '').trim()
  const at = Number.isInteger(caret) ? Math.min(Math.max(caret, 0), v.length) : v.length
  if (!w) return { value: v, caret: at }
  const before = v.slice(0, at)
  const after = v.slice(at)
  const lead = before && !/\s$/.test(before) ? ' ' : ''
  const trail = after && !/^\s/.test(after) ? ' ' : ''
  return { value: before + lead + w + trail + after, caret: at + lead.length + w.length }
}

function pickMime() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return ''
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'].find((t) => MediaRecorder.isTypeSupported(t)) || ''
}

// A request that never answers must not leave "transcribing…" on screen for ever: abort it and let the poll / the
// Try again button take over (the clip is kept). Older browsers without AbortSignal.timeout simply get no bound.
const UPLOAD_TIMEOUT_MS = 90 * 1000
const POLL_TIMEOUT_MS = 20 * 1000
function bounded(ms) {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(ms) : undefined
}

function csrf() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

const clock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

export default function DictateButton({ targetRef, onText, context, disabled = false, dark = false, className = '' }) {
  const [phase, setPhase] = useState('idle') // idle | recording | transcribing | error
  const [secs, setSecs] = useState(0)
  const [message, setMessage] = useState('')
  const [kept, setKept] = useState(null)     // { blob, ms } — held until it is transcribed or the user discards it
  const rec = useRef(null)
  const chunks = useRef([])
  const stream = useRef(null)
  const tick = useRef(null)
  const pollTimer = useRef(null)
  const started = useRef(0)
  const alive = useRef(true)
  const touched = useRef(false)              // has the user put the cursor in the box? (else the words go at the end)
  const onTextRef = useRef(onText)           // the latest setter, even when the words land minutes after the tap
  onTextRef.current = onText

  useEffect(() => {
    alive.current = true
    const el = targetRef?.current
    const mark = () => { touched.current = true }
    const evs = ['focus', 'click', 'keyup', 'select', 'input']
    evs.forEach((e) => el?.addEventListener(e, mark))
    return () => {
      alive.current = false
      evs.forEach((e) => el?.removeEventListener(e, mark))
      clearInterval(tick.current)
      clearTimeout(pollTimer.current)
      if (rec.current && rec.current.state !== 'inactive') { try { rec.current.stop() } catch { /* already stopped */ } }
      stopTracks()
    }
  }, [])

  // Closing the tab while a recording is held or being transcribed asks first.
  useEffect(() => {
    if (phase === 'idle' && !kept) return undefined
    const warn = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [phase, kept])

  function stopTracks() {
    stream.current?.getTracks?.().forEach((t) => t.stop())
    stream.current = null
  }

  function fail(msg) {
    if (!alive.current) return
    setPhase('error')
    setMessage(msg || UNAVAILABLE)
  }

  async function start() {
    if (disabled || phase === 'recording' || phase === 'transcribing') return
    setMessage('')
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setPhase('error'); setMessage('This browser cannot record. Please type.'); return
    }
    let s
    try {
      s = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
    } catch (e) {
      setPhase('error')
      setMessage(e?.name === 'NotAllowedError' ? 'The microphone is blocked. Allow it for this site, or type.' : 'No microphone found. Please type.')
      return
    }
    if (!alive.current) { s.getTracks().forEach((t) => t.stop()); return }
    stream.current = s
    const mime = pickMime()
    let r
    try { r = new MediaRecorder(s, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: 32000 }) } catch { r = new MediaRecorder(s) }
    chunks.current = []
    r.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data) }
    r.onstop = () => {
      const ms = Date.now() - started.current
      clearInterval(tick.current)
      stopTracks()
      if (!alive.current) return
      const blob = new Blob(chunks.current, { type: (r.mimeType || mime || 'audio/webm').split(';')[0] })
      if (!blob.size) { setPhase('error'); setMessage('Nothing was recorded. Please try again.'); return }
      const clip = { blob, ms }
      setKept(clip)
      upload(clip)
    }
    rec.current = r
    started.current = Date.now()
    setSecs(0)
    r.start(1000)
    setPhase('recording')
    tick.current = setInterval(() => {
      const n = Math.floor((Date.now() - started.current) / 1000)
      setSecs(n)
      if (n >= MAX_SECONDS) stop()
    }, 250)
  }

  function stop() {
    clearInterval(tick.current)
    const r = rec.current
    if (r && r.state !== 'inactive') r.stop()
  }

  async function upload(clip) {
    setPhase('transcribing'); setMessage('')
    const ext = clip.blob.type.includes('mp4') ? 'm4a' : clip.blob.type.includes('ogg') ? 'ogg' : 'webm'
    const fd = new FormData()
    fd.append('audio', clip.blob, `dictation.${ext}`)
    fd.append('duration_ms', String(Math.round(clip.ms)))
    if (context) fd.append('context', context)
    try {
      const res = await fetch('/dictations', { method: 'POST', body: fd, credentials: 'same-origin',
        headers: { 'X-CSRF-Token': csrf(), Accept: 'application/json' }, signal: bounded(UPLOAD_TIMEOUT_MS) })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.id) return fail(data.message)
      poll(data.id, Date.now())
    } catch {
      fail(UNAVAILABLE)
    }
  }

  function poll(id, since) {
    clearTimeout(pollTimer.current)
    pollTimer.current = setTimeout(async () => {
      if (!alive.current) return
      try {
        const res = await fetch(`/dictations/${id}`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: bounded(POLL_TIMEOUT_MS) })
        const data = await res.json().catch(() => ({}))
        if (res.ok && data.status === 'transcribing') {
          return Date.now() - since > GIVE_UP_MS ? fail(UNAVAILABLE) : poll(id, since)
        }
        if (res.ok && data.ok && typeof data.text === 'string') return insert(data.text, data.unclear)
        return fail(data.message)
      } catch {
        // A network blip mid-wait: keep asking (the server holds the words for an hour), then give up with the clip kept.
        return Date.now() - since > GIVE_UP_MS ? fail(UNAVAILABLE) : poll(id, since)
      }
    }, POLL_MS)
  }

  function insert(words, unclear) {
    if (!alive.current) return
    const el = targetRef?.current
    const current = el ? el.value : ''
    const caret = el && touched.current ? el.selectionEnd : current.length
    const next = insertAtCursor(current, caret, words)
    onTextRef.current?.(next.value)
    setKept(null)
    setPhase('idle')
    setMessage(unclear ? 'Added, but the transcriber was unsure. Please check it before saving.' : '')
    setTimeout(() => {
      const box = targetRef?.current
      if (!box) return
      try { box.focus(); box.setSelectionRange(next.caret, next.caret) } catch { /* not a text box */ }
    }, 30)
  }

  function discard() {
    setKept(null); setPhase('idle'); setMessage('')
  }

  const btn = dark
    ? 'border-white/30 bg-white/10 text-white hover:bg-white/20'
    : 'border-brand-border bg-white text-brand-ink hover:border-brand-primary hover:text-brand-primary'
  const note = dark ? 'text-white/70' : 'text-brand-muted'

  return (
    <div className={`flex flex-wrap items-center gap-2 text-xs ${className}`} data-dictate>
      {phase === 'recording' && (
        <button type="button" onClick={stop} aria-label="Stop dictating"
          className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 font-semibold text-white shadow-sm active:scale-95">
          <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
          <Square size={11} className="fill-current" /> Stop · {clock(secs)} / {clock(MAX_SECONDS)}
        </button>
      )}
      {phase === 'transcribing' && (
        <span className={`inline-flex items-center gap-1.5 ${note}`} role="status">
          <Loader2 size={14} className="animate-spin" /> transcribing…
        </span>
      )}
      {(phase === 'idle' || (phase === 'error' && !kept)) && (
        <button type="button" onClick={start} disabled={disabled} aria-label="Dictate"
          title="Dictate: speak and the words are typed in here (transcribed on the practice's own computer)"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-medium active:scale-95 disabled:opacity-50 ${btn}`}>
          <Mic size={14} /> Dictate
        </button>
      )}
      {phase === 'error' && kept && (
        <>
          <button type="button" onClick={() => upload(kept)}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-medium active:scale-95 ${btn}`}>
            <RotateCcw size={12} /> Try again
          </button>
          <button type="button" onClick={discard}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-medium active:scale-95 ${btn}`}>
            <X size={12} /> Discard recording
          </button>
        </>
      )}
      {message && <span className={phase === 'error' ? 'font-medium text-red-600' : note} role="status">{message}</span>}
    </div>
  )
}
